import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { EmptyState } from '../components/ui/EmptyState'
import { MoreActionsMenu } from '../components/ui/MoreActionsMenu'
import { SectionTabPanel, SectionTabs, useSectionTab } from '../components/ui/SectionTabs'
import { listGovernanceRegisterPositions, type GovernanceRegisterPosition, type GovernanceStageKey } from '../governance/governance-api'
import { stateLabel } from '../lib/display-labels'
import { useTenant } from '../tenancy/TenantProvider'
import { ErrorAlert, operationalStateLabels, PageHeader } from '../portfolio/PortfolioCommon'
import {
  createAiUseDraft,
  abandonAiUseDraft,
  getAiUseEntryOptions,
  getRegisterSummary,
  listMyAiUseDrafts,
  listRegisterEntries,
  type RegisterEntry,
} from './register-api'
import { listAiUsePrivateProducts } from './private-product-links-api'
import { DiscardDraftDialog } from './DiscardDraftDialog'

const PAGE_SIZE = 20

type SummaryFilter = 'all' | 'live' | 'attention' | 'missing'
type SortMode = 'attention' | 'updated' | 'name'

const governanceStageLabels: Record<GovernanceStageKey, string> = {
  register: 'Register', assess: 'Assess', control: 'Control', approve: 'Approve', monitor: 'Monitor', review: 'Review',
}
const governanceAttentionStates: Record<GovernanceStageKey, string[]> = {
  register: ['needs_attention'],
  assess: ['not_started', 'draft', 'in_progress', 'reassessment_in_progress', 'submitted', 'evaluated', 'reassessment_required'],
  control: ['needs_attention', 'overdue'],
  approve: ['reapproval_required', 'not_ready', 'awaiting_approval', 'changes_requested', 'rejected'],
  monitor: ['active', 'needs_attention', 'overdue'],
  review: ['due', 'overdue', 'in_progress', 'completed_actions_required', 'completed_reassessment_required'],
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function strategyLabel(entry: RegisterEntry) {
  return entry.strategyAlignment === 'aligned' ? 'Aligned' : 'Not yet aligned'
}

const governanceRowStageLabels: Record<GovernanceStageKey, string> = {
  register: 'Register', assess: 'Assessment', control: 'Controls', approve: 'Approval', monitor: 'Monitoring', review: 'Review',
}

const nextActionLabels: Record<string, string> = {
  'Register information is incomplete': 'Complete the missing register information',
  'Register information required': 'Complete the missing register information',
  'Formal Assessment requires attention': 'Review the formal assessment',
  'Reassessment is required': 'Complete the reassessment',
  'Required Controls are overdue': 'Address the overdue required controls',
  'Required Controls are outstanding': 'Complete the outstanding required controls',
  'Reapproval is required': 'Complete reapproval',
  'Approval decision is pending': 'Await the approval decision',
  'Approval was rejected': 'Review the rejected approval decision',
  'Approval requires attention': 'Resolve the approval issue',
  'Monitoring obligations are overdue': 'Address the overdue monitoring obligations',
  'Monitoring obligations require attention': 'Review the monitoring obligations',
  'Periodic Review is overdue': 'Complete the overdue periodic review',
  'Periodic Review is due': 'Complete the periodic review',
  'Review requires reassessment': 'Complete the required reassessment',
  'Review actions are required': 'Complete the required review actions',
  'Periodic Review is in progress': 'Continue the periodic review',
}

function governanceStatusLine(position?: GovernanceRegisterPosition) {
  if (!position) return null
  const relevantStages = (Object.keys(governanceRowStageLabels) as GovernanceStageKey[])
    .filter((stage) => governanceAttentionStates[stage].includes(position.stages[stage].state))
    .slice(0, 2)
  if (!relevantStages.length) return null
  return relevantStages
    .map((stage) => `${governanceRowStageLabels[stage]} ${stateLabel(position.stages[stage].state).toLocaleLowerCase('en-AU')}`)
    .join(' · ')
}

function RegisterStatus({ entry, position }: { entry: RegisterEntry; position?: GovernanceRegisterPosition }) {
  const governanceLine = governanceStatusLine(position)
  const nextAction = position?.highestPriorityAttentionReason
    ? nextActionLabels[position.highestPriorityAttentionReason] ?? position.highestPriorityAttentionReason
    : null
  return (
    <div className="register-status">
      <span className="register-status-primary">{operationalStateLabels[entry.operationalState] ?? stateLabel(entry.operationalState)} · {strategyLabel(entry)}</span>
      {governanceLine ? <span className="register-status-current">{governanceLine}</span> : null}
      {nextAction ? <span className="register-supporting-text">Next: {nextAction}</span> : null}
    </div>
  )
}

function compareEntries(
  first: RegisterEntry,
  second: RegisterEntry,
  sort: SortMode,
  positionByUse: Map<string, GovernanceRegisterPosition>,
) {
  if (sort === 'name') return first.title.localeCompare(second.title)
  if (sort === 'updated') return new Date(second.lastMaterialUpdateAt).getTime() - new Date(first.lastMaterialUpdateAt).getTime()

  const score = (entry: RegisterEntry) => (
    Number(entry.summaryNeedsAttention) * 4
    + Number(entry.summaryOwnerOrInformationMissing) * 2
    + Number(Boolean(positionByUse.get(entry.id)?.highestPriorityAttentionReason))
  )
  return score(second) - score(first)
    || new Date(second.lastMaterialUpdateAt).getTime() - new Date(first.lastMaterialUpdateAt).getTime()
    || first.title.localeCompare(second.title)
}

export function RegisterLandingPage() {
  const { current } = useTenant()
  const organisationId = current?.organisation.id ?? ''
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [businessUnitId, setBusinessUnitId] = useState('')
  const [operationalStatus, setOperationalStatus] = useState('')
  const [primaryActivity, setPrimaryActivity] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [alignment, setAlignment] = useState(() => ['aligned', 'not_yet_aligned'].includes(searchParams.get('alignment') ?? '') ? searchParams.get('alignment') ?? '' : '')
  const [summaryFilter, setSummaryFilter] = useState<SummaryFilter>(() => ['live', 'attention', 'missing'].includes(searchParams.get('summary') ?? '') ? searchParams.get('summary') as SummaryFilter : 'all')
  const [sort, setSort] = useState<SortMode>('attention')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [visibleUseCount, setVisibleUseCount] = useState(PAGE_SIZE)
  const [visibleDraftCount, setVisibleDraftCount] = useState(PAGE_SIZE)
  const [discardingDraft, setDiscardingDraft] = useState<{ id: string; name: string } | null>(null)
  const filterShellRef = useRef<HTMLDivElement>(null)
  const filterButtonRef = useRef<HTMLButtonElement>(null)
  const governanceStage = (searchParams.get('governance') ?? '') as GovernanceStageKey | ''
  const optionsQuery = useQuery({ queryKey: ['ai-use-entry-options', organisationId], queryFn: () => getAiUseEntryOptions(organisationId), enabled: Boolean(organisationId) })
  const summaryQuery = useQuery({ queryKey: ['ai-register-summary', organisationId], queryFn: () => getRegisterSummary(organisationId), enabled: Boolean(organisationId) })
  const entriesQuery = useQuery({
    queryKey: ['ai-register', organisationId, search, businessUnitId, operationalStatus, primaryActivity, ownerId, alignment],
    queryFn: () => listRegisterEntries({ organisationId, search, businessUnitId, operationalState: operationalStatus, primaryActivity, ownerMembershipId: ownerId, alignment }),
    enabled: Boolean(organisationId),
  })
  const privateProductsQuery = useQuery({
    queryKey: ['ai-use-private-products', organisationId],
    queryFn: () => listAiUsePrivateProducts(organisationId),
    enabled: Boolean(organisationId),
  })
  const governancePositionsQuery = useQuery({
    queryKey: ['phase3-register-governance-positions', organisationId],
    queryFn: () => listGovernanceRegisterPositions(organisationId),
    enabled: Boolean(organisationId),
  })
  const draftsQuery = useQuery({ queryKey: ['ai-use-drafts', organisationId], queryFn: () => listMyAiUseDrafts(organisationId), enabled: Boolean(organisationId) })
  const startMutation = useMutation({ mutationFn: () => createAiUseDraft(organisationId, 'product_unknown'), onSuccess: (draftId) => void navigate(`/ai-register/add/${draftId}`) })
  const discardMutation = useMutation({
    mutationFn: (draftId: string) => abandonAiUseDraft(draftId),
    onSuccess: async () => {
      setDiscardingDraft(null)
      await queryClient.invalidateQueries({ queryKey: ['ai-use-drafts', organisationId] })
    },
  })

  const registerTabs = [
    { id: 'uses' as const, label: `AI uses (${summaryQuery.data?.total ?? '…'})` },
    { id: 'drafts' as const, label: `Drafts (${draftsQuery.data?.length ?? '…'})` },
  ]
  const [activeTab, setActiveTab] = useSectionTab(registerTabs, 'uses')

  useEffect(() => {
    const requestedSummary = searchParams.get('summary')
    setSummaryFilter(['live', 'attention', 'missing'].includes(requestedSummary ?? '') ? requestedSummary as SummaryFilter : 'all')
    const requestedAlignment = searchParams.get('alignment')
    setAlignment(['aligned', 'not_yet_aligned'].includes(requestedAlignment ?? '') ? requestedAlignment ?? '' : '')
  }, [searchParams])

  useEffect(() => {
    setVisibleUseCount(PAGE_SIZE)
  }, [search, businessUnitId, operationalStatus, primaryActivity, ownerId, alignment, governanceStage, summaryFilter, sort])

  useEffect(() => {
    if (!filtersOpen) return
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Node && !filterShellRef.current?.contains(event.target)) setFiltersOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setFiltersOpen(false)
      filterButtonRef.current?.focus()
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [filtersOpen])

  if (!current) return <Navigate to="/workspace/create" replace />

  const options = optionsQuery.data
  const entries = entriesQuery.data ?? []
  const privateProducts = privateProductsQuery.data ?? []
  const privateProductByUse = new Map(privateProducts.filter((item) => item.aiUseCaseId).map((item) => [item.aiUseCaseId as string, item]))
  const governancePositions = governancePositionsQuery.data ?? []
  const positionByUse = new Map(governancePositions.map((position) => [position.aiUseCaseId, position]))
  const governanceFilteredEntries = governanceStage
    ? entries.filter((entry) => governanceAttentionStates[governanceStage]?.includes(positionByUse.get(entry.id)?.stages[governanceStage].state ?? ''))
    : entries
  const summaryFilteredEntries = governanceFilteredEntries.filter((entry) => {
    if (summaryFilter === 'live') return ['pilot', 'production'].includes(entry.operationalState)
    if (summaryFilter === 'attention') return entry.summaryNeedsAttention
    if (summaryFilter === 'missing') return entry.summaryOwnerOrInformationMissing
    return true
  })
  const sortedEntries = [...summaryFilteredEntries].sort((first, second) => compareEntries(first, second, sort, positionByUse))
  const visibleEntries = sortedEntries.slice(0, visibleUseCount)
  const summary = summaryQuery.data
  const drafts = draftsQuery.data ?? []
  const visibleDrafts = drafts.slice(0, visibleDraftCount)

  const setGovernanceStage = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set('governance', value)
    else next.delete('governance')
    setSearchParams(next, { replace: true })
  }

  const resetFilters = () => {
    setSearch('')
    setBusinessUnitId('')
    setOperationalStatus('')
    setPrimaryActivity('')
    setOwnerId('')
    setAlignment('')
    setSummaryFilter('all')
    setGovernanceStage('')
  }

  const activeFilters = [
    businessUnitId ? { id: 'business-area', label: 'Business area', value: options?.businessUnits.find((item) => item.id === businessUnitId)?.name ?? 'Selected', remove: () => setBusinessUnitId('') } : null,
    operationalStatus ? { id: 'operational-status', label: 'Operational status', value: operationalStateLabels[operationalStatus as keyof typeof operationalStateLabels] ?? operationalStatus, remove: () => setOperationalStatus('') } : null,
    primaryActivity ? { id: 'primary-activity', label: 'Primary AI activity', value: options?.classificationSets.ai_activity?.find((item) => item.valueCode === primaryActivity)?.label ?? 'Selected', remove: () => setPrimaryActivity('') } : null,
    ownerId ? { id: 'owner', label: 'Accountable owner', value: options?.members.find((item) => item.membershipId === ownerId)?.displayName ?? 'Selected', remove: () => setOwnerId('') } : null,
    alignment ? { id: 'alignment', label: 'Strategic alignment', value: alignment === 'aligned' ? 'Aligned' : 'Not yet aligned', remove: () => setAlignment('') } : null,
    governanceStage ? { id: 'governance', label: 'Governance status', value: governanceStageLabels[governanceStage], remove: () => setGovernanceStage('') } : null,
  ].filter((filter): filter is NonNullable<typeof filter> => Boolean(filter))

  const isLoadingUses = entriesQuery.isLoading || privateProductsQuery.isLoading || governancePositionsQuery.isLoading
  const hasFilters = Boolean(search || activeFilters.length || summaryFilter !== 'all')

  const productContext = (entry: RegisterEntry) => {
    const privateProduct = privateProductByUse.get(entry.id)
    return [entry.solutionName, entry.productName, privateProduct?.productName, entry.primaryActivityLabel]
      .find((value) => value?.trim())?.trim() ?? 'Product not recorded'
  }

  return (
    <div className="page-stack register-page register-landing-page">
      <PageHeader
        eyebrow="AI Register"
        title="AI Register"
        description="Keep a record of where AI is being used, tested or considered across your organisation."
        action={<button className="button primary" type="button" onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>Add an AI use</button>}
      />

      <div className="register-primary-tabs">
        <SectionTabs tabs={registerTabs} activeTab={activeTab} onSelect={setActiveTab} label="AI Register records" idPrefix="ai-register" />
      </div>

      <ErrorAlert error={optionsQuery.error ?? summaryQuery.error ?? entriesQuery.error ?? privateProductsQuery.error ?? governancePositionsQuery.error ?? draftsQuery.error ?? startMutation.error ?? discardMutation.error} />

      <SectionTabPanel idPrefix="ai-register" tab="uses" activeTab={activeTab}>
        <section className="register-summary-filters" aria-label="Filter AI uses by summary">
          {([
            ['all', 'All', summary?.total],
            ['live', 'Live or in pilot', summary?.liveOrPilot],
            ['attention', 'Needs attention', summary?.needsAttention],
            ['missing', 'Missing information', summary?.ownerOrInformationMissing],
          ] as const).map(([id, label, count]) => (
            <button key={id} type="button" className="register-summary-filter" aria-pressed={summaryFilter === id} onClick={() => setSummaryFilter(id)}>
              <span>{label}</span><strong aria-hidden="true">·</strong><span>{count ?? '…'}</span>
            </button>
          ))}
        </section>

        <section className="register-controls" aria-label="Search, filter and sort AI uses">
          <div className="register-toolbar">
            <label className="register-search"><span>Search AI uses</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search AI uses" /></label>
            <div className="register-filter-shell" ref={filterShellRef}>
              <button ref={filterButtonRef} className="button secondary compact" type="button" aria-expanded={filtersOpen} aria-controls="register-filter-panel" onClick={() => setFiltersOpen((open) => !open)}>
                Filters{activeFilters.length ? ` (${activeFilters.length})` : ''}
              </button>
              {filtersOpen ? (
                <div className="register-filter-popover" id="register-filter-panel" role="region" aria-label="AI use filters">
                  <div className="register-filter-popover-heading"><strong>Filters</strong><button className="button text compact" type="button" onClick={() => { setFiltersOpen(false); filterButtonRef.current?.focus() }}>Close</button></div>
                  <div className="register-filter-grid">
                    <label><span>Business area</span><select value={businessUnitId} onChange={(event) => setBusinessUnitId(event.target.value)}><option value="">All</option>{options?.businessUnits.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}</select></label>
                    <label><span>Operational status</span><select value={operationalStatus} onChange={(event) => setOperationalStatus(event.target.value)}><option value="">All</option>{Object.entries(operationalStateLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                    <label><span>Primary AI activity</span><select value={primaryActivity} onChange={(event) => setPrimaryActivity(event.target.value)}><option value="">All</option>{options?.classificationSets.ai_activity?.map((option) => <option key={option.valueCode} value={option.valueCode}>{option.label}</option>)}</select></label>
                    <label><span>Accountable owner</span><select value={ownerId} onChange={(event) => setOwnerId(event.target.value)}><option value="">All</option>{options?.members.map((member) => <option key={member.membershipId} value={member.membershipId}>{member.displayName}</option>)}</select></label>
                    <label><span>Strategic alignment</span><select value={alignment} onChange={(event) => setAlignment(event.target.value)}><option value="">All</option><option value="aligned">Aligned</option><option value="not_yet_aligned">Not yet aligned</option></select></label>
                    <label><span>Governance status</span><select value={governanceStage} onChange={(event) => setGovernanceStage(event.target.value)}><option value="">All</option>{Object.entries(governanceStageLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                  </div>
                </div>
              ) : null}
            </div>
            <label className="register-sort"><span>Sort</span><select value={sort} onChange={(event) => setSort(event.target.value as SortMode)}><option value="attention">Needs attention</option><option value="updated">Updated date</option><option value="name">Name</option></select></label>
            <p className="register-result-count" aria-live="polite">{sortedEntries.length} {sortedEntries.length === 1 ? 'result' : 'results'}</p>
          </div>
          {activeFilters.length ? (
            <div className="register-active-filters" aria-label="Active filters">
              {activeFilters.map((filter) => <button key={filter.id} type="button" onClick={filter.remove} aria-label={`Remove ${filter.label} filter: ${filter.value}`}>{filter.label}: {filter.value}<span aria-hidden="true">×</span></button>)}
              <button className="register-clear-filters" type="button" onClick={resetFilters}>Clear all</button>
            </div>
          ) : null}
        </section>

        {isLoadingUses ? <section className="panel"><p>Loading AI Register…</p></section> : null}
        {!isLoadingUses && sortedEntries.length === 0 ? (
          <section className="panel register-empty-state">
            {hasFilters || (summary?.total ?? 0) > 0 ? (
              <EmptyState icon="register" eyebrow="No matches" title="No AI uses match these filters" description="Try clearing the filters or changing the search. Your recorded AI uses have not been removed." actions={<button className="button secondary" type="button" onClick={resetFilters}>Clear filters</button>} />
            ) : (
              <EmptyState icon="register" eyebrow="AI Register" title="No AI uses recorded yet" description="Use Add an AI use at the top of the page to record the first identifiable business use." />
            )}
          </section>
        ) : null}

        {visibleEntries.length > 0 ? (
          <section className="panel register-records-panel">
            <div className="register-table-wrap">
              <table className="register-table">
                <caption className="register-visually-hidden">Registered AI uses</caption>
                <colgroup><col className="register-col-ai-use" /><col className="register-col-accountability" /><col className="register-col-status" /><col className="register-col-updated" /></colgroup>
                <thead><tr><th scope="col">AI use</th><th scope="col">Accountability</th><th scope="col">Status</th><th scope="col">Updated</th></tr></thead>
                <tbody>{visibleEntries.map((entry) => (
                  <tr key={entry.id}>
                    <td><Link className="register-record-link" to={`/ai-register/${entry.id}`}>{entry.title}</Link><span className="register-supporting-text">{productContext(entry)}</span></td>
                    <td><span>{entry.businessUnitName?.trim() || 'Business area not recorded'}</span><span className="register-supporting-text">{entry.businessOwnerName?.trim() ? `Owner: ${entry.businessOwnerName}` : 'Owner not assigned'}</span></td>
                    <td><RegisterStatus entry={entry} position={positionByUse.get(entry.id)} /></td>
                    <td className="register-updated-date">{formatDate(entry.lastMaterialUpdateAt)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>

            <div className="register-card-list" aria-label="Registered AI uses">
              {visibleEntries.map((entry) => (
                <article className="register-use-card" key={entry.id}>
                  <div className="register-use-card-heading"><div><Link className="register-record-link" to={`/ai-register/${entry.id}`}>{entry.title}</Link><span className="register-supporting-text">{productContext(entry)}</span></div></div>
                  <dl>
                    <div><dt>Accountability</dt><dd>{entry.businessUnitName?.trim() || 'Business area not recorded'}<span className="register-supporting-text">{entry.businessOwnerName?.trim() ? `Owner: ${entry.businessOwnerName}` : 'Owner not assigned'}</span></dd></div>
                    <div className="register-use-card-status"><dt>Status</dt><dd><RegisterStatus entry={entry} position={positionByUse.get(entry.id)} /></dd></div>
                    <div><dt>Updated</dt><dd className="register-updated-date">{formatDate(entry.lastMaterialUpdateAt)}</dd></div>
                  </dl>
                </article>
              ))}
            </div>

            {visibleUseCount < sortedEntries.length ? <button className="button secondary register-load-more" type="button" onClick={() => setVisibleUseCount((count) => count + PAGE_SIZE)}>Load more AI uses</button> : null}
          </section>
        ) : null}
      </SectionTabPanel>

      <SectionTabPanel idPrefix="ai-register" tab="drafts" activeTab={activeTab}>
        <section className="panel register-drafts-panel">
          <div className="section-heading"><div><p className="eyebrow">Private working records</p><h2>Draft AI uses</h2><p>Drafts are private working records until added to the Register. Missing information can remain clearly marked instead of being guessed.</p></div></div>
          {draftsQuery.isLoading ? <p>Loading drafts…</p> : null}
          {!draftsQuery.isLoading && drafts.length === 0 ? <EmptyState icon="register" title="No draft AI uses" description="Unfinished AI uses will appear here so you can continue where you left off." compact /> : null}
          {visibleDrafts.length ? (
            <div className="register-draft-list" role="list">
              {visibleDrafts.map((draft, index) => {
                const name = draft.name?.trim() || `Untitled AI use ${index + 1}`
                return <div className="register-draft-row" role="listitem" key={draft.id}>
                  <Link className="register-record-link" to={`/ai-register/add/${draft.id}`}>{name}</Link>
                  <span>Step {Math.max(1, draft.currentStep)} of 5</span>
                  <span>Updated {formatDate(draft.updatedAt)}</span>
                  <MoreActionsMenu label={`More actions for draft ${name}`} actions={[{ id: 'discard', label: 'Discard draft', danger: true, onSelect: () => setDiscardingDraft({ id: draft.id, name }) }]} />
                </div>
              })}
            </div>
          ) : null}
          {visibleDraftCount < drafts.length ? <button className="button secondary register-load-more" type="button" onClick={() => setVisibleDraftCount((count) => count + PAGE_SIZE)}>Load more drafts</button> : null}
        </section>
      </SectionTabPanel>
      {discardingDraft ? <DiscardDraftDialog name={discardingDraft.name} pending={discardMutation.isPending} onClose={() => setDiscardingDraft(null)} onConfirm={() => discardMutation.mutate(discardingDraft.id)} /> : null}
    </div>
  )
}
